'use client';

// Warms the route an AppLink points to. Prefetching is off by default; each
// call site explicitly opts in via the `mode` prop. Modes are mutually
// exclusive so the policy at each call site is unambiguous.
//
// Modes:
//   'none'     — never prefetch (default)
//   'hover'    — prefetch on pointer hover or keyboard focus
//   'viewport' — prefetch when the link scrolls within rootMargin of viewport
//   'instant'  — prefetch immediately on mount (bypass observer + intent)
//
// All modes respect bot detection (useIsBot), Save-Data, and 2g/slow-2g
// connection hints — those gates prevent prefetch regardless of mode.
//
// Bot suppression is the reason this hook exists: Next.js <Link prefetch>
// emits `?_rsc=<hash>` URLs that Googlebot's WRS would otherwise discover
// and index as duplicates of their canonical pages.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsBot } from './useIsBot';

type NetworkInformation = {
  saveData?: boolean;
  effectiveType?: string;
};

const getConnection = (): NetworkInformation | null => {
  if (typeof navigator === 'undefined') return null;
  return (navigator as Navigator & { connection?: NetworkInformation }).connection ?? null;
};

const hasRIC = typeof window !== 'undefined' && 'requestIdleCallback' in window;

const requestIdle = (cb: () => void): number =>
  hasRIC
    ? window.requestIdleCallback(cb, { timeout: 2000 })
    : requestAnimationFrame(() => window.setTimeout(cb, 500));

const cancelIdle = (id: number): void => {
  if (hasRIC) {
    window.cancelIdleCallback(id);
  } else {
    window.clearTimeout(id);
  }
};

/** ------------ types ------------ */
export type PrefetchMode = 'none' | 'hover' | 'viewport' | 'instant';

export type UseLinkPrefetchOptions = {
  /** Prefetch policy. Default 'none'. */
  mode?: PrefetchMode;

  /** IntersectionObserver root margin (viewport mode only). Default '400px'. */
  rootMargin?: string;

  /** Hover dwell time before prefetch (hover mode only). Default 80 ms. */
  hoverDelayMs?: number;
};

type BindProps = {
  ref: (el: HTMLElement | null) => void;
  onPointerEnter?: (e: any) => void;
  onPointerLeave?: (e: any) => void;
  onFocus?: (e: any) => void;
};

export type UseLinkPrefetchResult = Readonly<BindProps> & {
  /** Becomes true once intent/visibility/instant fires — feed into <Link prefetch>. */
  shouldPrefetch: boolean;
};

/** ------------ hook ------------ */
export const useLinkPrefetch = ({
  mode = 'none',
  rootMargin = '400px',
  hoverDelayMs = 80,
}: UseLinkPrefetchOptions = {}): UseLinkPrefetchResult => {
  const isBot = useIsBot();
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const elRef = useRef<HTMLElement | null>(null);
  const ioRef = useRef<IntersectionObserver | null>(null);
  const idleIdRef = useRef<number | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const { saveData, isVerySlow } = useMemo(() => {
    const conn = getConnection();
    return {
      saveData: !!conn?.saveData,
      isVerySlow: conn?.effectiveType === '2g' || conn?.effectiveType === 'slow-2g',
    };
  }, []);

  // Touch devices have no real hover — skip pointer prefetch but keep focus.
  const isTouchOnly = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      ((navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0
    );
  }, []);

  const canPrefetch = !isBot && !saveData && !isVerySlow;

  const enable = useCallback(() => {
    if (!canPrefetch) return;
    setShouldPrefetch(true);
  }, [canPrefetch]);

  // Visible / hover prefetch waits for idle time before flipping the prop.
  const enableWhenIdle = useCallback(() => {
    if (!canPrefetch) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (idleIdRef.current != null) return;
    idleIdRef.current = requestIdle(() => {
      idleIdRef.current = null;
      enable();
    });
  }, [canPrefetch, enable]);

  const setupVisibilityObserver = useCallback(
    (el: HTMLElement) => {
      if (mode !== 'viewport' || !canPrefetch) return;
      if (typeof IntersectionObserver === 'undefined') {
        // No IntersectionObserver — best-effort fall back to an idle prefetch.
        enableWhenIdle();
        return;
      }

      ioRef.current?.disconnect();
      ioRef.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            ioRef.current?.disconnect();
            ioRef.current = null;
            enableWhenIdle();
          }
        },
        { rootMargin },
      );
      ioRef.current.observe(el);
    },
    [mode, canPrefetch, rootMargin, enableWhenIdle],
  );

  // Instant mode: prefetch as soon as bot detection clears.
  useEffect(() => {
    if (mode !== 'instant') return;
    if (!canPrefetch) return;
    if (shouldPrefetch) return;
    setShouldPrefetch(true);
  }, [mode, canPrefetch, shouldPrefetch]);

  // Viewport mode: (re-)setup observer when canPrefetch flips (post-hydration).
  useEffect(() => {
    if (mode !== 'viewport') return;
    if (shouldPrefetch) return;
    const el = elRef.current;
    if (!el) return;
    setupVisibilityObserver(el);
    return () => {
      ioRef.current?.disconnect();
      ioRef.current = null;
    };
  }, [mode, shouldPrefetch, setupVisibilityObserver]);

  // Bindings
  const bind: BindProps = {
    ref: (el) => {
      elRef.current = el;
      if (el && mode === 'viewport' && !shouldPrefetch) {
        setupVisibilityObserver(el);
      } else if (!el) {
        ioRef.current?.disconnect();
        ioRef.current = null;
      }
    },
  };

  // Hover mode: pointer + focus handlers
  if (mode === 'hover') {
    if (!isTouchOnly) {
      bind.onPointerEnter = () => {
        if (hoverTimerRef.current != null) return;
        hoverTimerRef.current = window.setTimeout(() => {
          hoverTimerRef.current = null;
          enable();
        }, hoverDelayMs);
      };
      bind.onPointerLeave = () => {
        if (hoverTimerRef.current != null) {
          window.clearTimeout(hoverTimerRef.current);
          hoverTimerRef.current = null;
        }
      };
    }
    // Focus always allowed for keyboard a11y, including touch users.
    bind.onFocus = () => enable();
  }

  // Cleanup timers and observer on unmount.
  useEffect(
    () => () => {
      if (idleIdRef.current != null) cancelIdle(idleIdRef.current);
      if (hoverTimerRef.current != null) window.clearTimeout(hoverTimerRef.current);
      ioRef.current?.disconnect();
    },
    [],
  );

  return { ...bind, shouldPrefetch };
};
